/**
 * Monitor de Performance - JurisPrazo V2
 * Monitora performance e uso de memória
 */

import cacheService from '../services/cacheService';

class PerformanceMonitor {
  constructor() {
    this.metrics = {
      loadTimes: [],
      apiCalls: [],
      cacheHits: 0,
      cacheMisses: 0,
      memoryUsage: []
    };
    
    this.startTime = Date.now();
    this.isEnabled = process.env.NODE_ENV === 'development';
  }

  /**
   * Marcar início de uma operação
   */
  startOperation(name) {
    if (!this.isEnabled) return null;
    
    const startTime = performance.now();
    console.log(`⏱️ [PERF] Iniciando: ${name}`);
    
    return {
      name,
      startTime,
      end: () => this.endOperation(name, startTime)
    };
  }

  /**
   * Marcar fim de uma operação
   */
  endOperation(name, startTime) {
    if (!this.isEnabled) return;
    
    const endTime = performance.now();
    const duration = Math.round(endTime - startTime);
    
    this.metrics.loadTimes.push({
      name,
      duration,
      timestamp: Date.now()
    });
    
    // Alertar se operação demorou muito
    if (duration > 3000) {
      console.warn(`⚠️ [PERF] LENTO: ${name} demorou ${duration}ms`);
    } else if (duration > 1000) {
      console.log(`🐌 [PERF] ${name}: ${duration}ms`);
    } else {
      console.log(`⚡ [PERF] ${name}: ${duration}ms`);
    }
  }

  /**
   * Registrar chamada de API
   */
  recordApiCall(endpoint, duration, success = true) {
    if (!this.isEnabled) return;
    
    this.metrics.apiCalls.push({
      endpoint,
      duration,
      success,
      timestamp: Date.now()
    });
  }

  /**
   * Registrar hit/miss de cache
   */
  recordCacheHit() {
    this.metrics.cacheHits++;
  }

  recordCacheMiss() {
    this.metrics.cacheMisses++;
  }

  /**
   * Monitorar uso de memória
   */
  recordMemoryUsage() {
    if (!this.isEnabled) return;
    
    if (performance.memory) {
      const memory = {
        used: Math.round(performance.memory.usedJSHeapSize / 1024 / 1024), // MB
        total: Math.round(performance.memory.totalJSHeapSize / 1024 / 1024), // MB
        limit: Math.round(performance.memory.jsHeapSizeLimit / 1024 / 1024), // MB
        timestamp: Date.now()
      };
      
      this.metrics.memoryUsage.push(memory);
      
      // Alertar se uso de memória estiver alto
      const usage = (memory.used / memory.limit) * 100;
      if (usage > 80) {
        console.warn(`🚨 [PERF] MEMÓRIA ALTA: ${usage.toFixed(1)}% (${memory.used}MB/${memory.limit}MB)`);
      }
      
      // Manter apenas últimas 50 medições
      if (this.metrics.memoryUsage.length > 50) {
        this.metrics.memoryUsage = this.metrics.memoryUsage.slice(-50);
      }
    }
  }

  /**
   * Obter relatório de performance
   */
  getReport() {
    if (!this.isEnabled) return null;
    
    const now = Date.now();
    const uptime = Math.round((now - this.startTime) / 1000);
    
    // Calcular médias
    const avgLoadTime = this.metrics.loadTimes.length > 0 
      ? Math.round(this.metrics.loadTimes.reduce((acc, t) => acc + t.duration, 0) / this.metrics.loadTimes.length)
      : 0;
    
    const cacheHitRate = this.metrics.cacheHits + this.metrics.cacheMisses > 0
      ? Math.round((this.metrics.cacheHits / (this.metrics.cacheHits + this.metrics.cacheMisses)) * 100)
      : 0;
    
    const currentMemory = this.metrics.memoryUsage.length > 0
      ? this.metrics.memoryUsage[this.metrics.memoryUsage.length - 1]
      : null;
    
    // Operações mais lentas
    const slowestOperations = this.metrics.loadTimes
      .sort((a, b) => b.duration - a.duration)
      .slice(0, 5);
    
    // Cache stats
    const cacheStats = cacheService.getStats();
    
    return {
      uptime,
      avgLoadTime,
      cacheHitRate,
      totalOperations: this.metrics.loadTimes.length,
      totalApiCalls: this.metrics.apiCalls.length,
      currentMemory,
      slowestOperations,
      cacheStats,
      recommendations: this.getRecommendations()
    };
  }

  /**
   * Obter recomendações de otimização
   */
  getRecommendations() {
    const recommendations = [];
    
    // Verificar operações lentas
    const slowOps = this.metrics.loadTimes.filter(op => op.duration > 2000);
    if (slowOps.length > 0) {
      recommendations.push(`🐌 ${slowOps.length} operações lentas detectadas (>2s)`);
    }
    
    // Verificar cache hit rate
    const cacheHitRate = this.metrics.cacheHits + this.metrics.cacheMisses > 0
      ? (this.metrics.cacheHits / (this.metrics.cacheHits + this.metrics.cacheMisses)) * 100
      : 0;
    
    if (cacheHitRate < 70) {
      recommendations.push(`📦 Cache hit rate baixo: ${cacheHitRate.toFixed(1)}% (ideal >70%)`);
    }
    
    // Verificar memória
    if (this.metrics.memoryUsage.length > 0) {
      const lastMemory = this.metrics.memoryUsage[this.metrics.memoryUsage.length - 1];
      const usage = (lastMemory.used / lastMemory.limit) * 100;
      if (usage > 70) {
        recommendations.push(`🚨 Uso de memória alto: ${usage.toFixed(1)}%`);
      }
    }
    
    // Verificar cache size
    const cacheStats = cacheService.getStats();
    if (cacheStats.memory > 10240) { // 10MB
      recommendations.push(`📦 Cache muito grande: ${Math.round(cacheStats.memory/1024)}MB`);
    }
    
    return recommendations;
  }

  /**
   * Imprimir relatório no console
   */
  printReport() {
    if (!this.isEnabled) return;
    
    const report = this.getReport();
    
    console.group('📊 RELATÓRIO DE PERFORMANCE');
    console.log(`⏱️ Uptime: ${report.uptime}s`);
    console.log(`⚡ Tempo médio: ${report.avgLoadTime}ms`);
    console.log(`📦 Cache hit rate: ${report.cacheHitRate}%`);
    console.log(`🔄 Total operações: ${report.totalOperations}`);
    console.log(`🌐 Total API calls: ${report.totalApiCalls}`);
    
    if (report.currentMemory) {
      console.log(`🧠 Memória: ${report.currentMemory.used}MB/${report.currentMemory.limit}MB`);
    }
    
    if (report.slowestOperations.length > 0) {
      console.log('🐌 Operações mais lentas:');
      report.slowestOperations.forEach((op, i) => {
        console.log(`  ${i+1}. ${op.name}: ${op.duration}ms`);
      });
    }
    
    if (report.recommendations.length > 0) {
      console.log('💡 Recomendações:');
      report.recommendations.forEach(rec => console.log(`  ${rec}`));
    }
    
    console.groupEnd();
  }

  /**
   * Iniciar monitoramento automático
   */
  startAutoMonitoring() {
    if (!this.isEnabled) return;
    
    // Monitorar memória a cada 30 segundos
    setInterval(() => {
      this.recordMemoryUsage();
    }, 30000);
    
    // Relatório a cada 5 minutos
    setInterval(() => {
      this.printReport();
    }, 5 * 60 * 1000);
    
    console.log('📊 Monitor de performance iniciado');
  }
}

// Instância singleton
const performanceMonitor = new PerformanceMonitor();

// Auto-iniciar em desenvolvimento
if (process.env.NODE_ENV === 'development') {
  performanceMonitor.startAutoMonitoring();
}

export default performanceMonitor;
